/**
 * metricInsightsAxisTicks - x-axis tick resolution for the Individual Metric
 * Insights chart.
 *
 * SE-7139: the chart asked victory-native for a tick *count* (`data.length`) and
 * let d3-scale choose where the ticks landed. d3 picks "nice round numbers"
 * across the domain, which is not the same thing as the seasons actually
 * plotted: for a player whose seasons are sparse or unevenly spaced, d3 coarsens
 * the step and the most recent season gets no tick at all — so its data point
 * renders with no year beneath it. Players whose season span happens to divide
 * evenly are unaffected, which is why the defect looked player-specific.
 *
 * Pinning the ticks to the season values in the data removes the choice. The
 * label under each point is the season that point belongs to, for every player,
 * on both platforms.
 */

/**
 * The x-axis key every Individual Metric Insights row carries.
 *
 * Exported so the View's `xKey` and this resolver read the same constant. When
 * they were two independent `"season"` literals, renaming the column would have
 * made this return `[]`, the caller would have omitted `tickValues`, and
 * SE-7139 would have come back silently.
 */
export const METRIC_INSIGHTS_X_KEY = "season";

/**
 * Resolve the x-axis tick values for the Individual Metric Insights chart.
 * @param rows - The merged chart rows, one per plotted season
 * @returns Ascending, de-duplicated season years — one tick per plotted season
 * @remarks
 * Rows are built by `buildChartData`, which already sorts by season and emits one
 * row per season, so in practice this is the season column verbatim. The sort and
 * de-duplication are kept anyway because `tickValues` is handed straight to
 * d3-scale, which draws ticks in array order and will happily draw the same year
 * twice.
 *
 * Ticks are sorted and de-duplicated because victory-native does neither: it
 * draws them in array order, and unlike Victory web (which calls `uniq`) it will
 * happily draw the same year twice.
 *
 * Non-numeric or non-finite season values are dropped rather than coerced: a NaN
 * tick makes victory-native throw ("TickValues array must only contain numbers"),
 * so a malformed row must not be able to take the whole chart down with it.
 *
 * An empty result is meaningful — it means there is nothing to label — and
 * callers MUST omit the `tickValues` prop entirely rather than pass `[]`. An
 * empty array is not "draw no ticks": victory-native derives the x domain from
 * the ticks, and `getDomainFromTicks([])` is `[Infinity, -Infinity]`, which makes
 * every plotted coordinate `NaN` on its way into Skia.
 */
export const resolveSeasonTickValues = (
  rows: readonly Record<string, unknown>[]
): number[] => {
  const seasons = rows
    .map(row => row[METRIC_INSIGHTS_X_KEY])
    .filter(
      (season): season is number =>
        typeof season === "number" && Number.isFinite(season)
    );

  return [...new Set(seasons)].sort((a, b) => a - b);
};
